// MACHINERY V2 (Stroje) — Špecifikácie tab (M2.5).
//
// Spec: data package §3 + ticket §5 #3 / §7 fix 5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`SpecsTab`); artboards
// 1006 (desktop), 1007 (the catalog-vs-custom tab-row diff), 1015 (phone).
// Rules = specSheet.ts, resolution = specResolve.ts, pixels = SpecSectionsV2 /
// specChrome / SpecDescription.
//
// THE TAB ONLY EXISTS FOR A CATALOG-LINKED MACHINE, and that gate is NOT here.
// profileTabContract.ts:`buildProfileTabs` pushes "specifications" only when
// `hasCatalogModel`, so a custom machine never renders this component at all —
// "the tab is not rendered (never rendered-empty)", §5 catalog-vs-custom rule,
// drawn in artboard 1007. Re-testing it here would be a second place to get it
// wrong.
//
// THE FETCH IS DE-DUPLICATED, NOT UNCONDITIONAL. `selectedModel` is shared with
// the catalog pages, so v1 skips the dispatch when the slot already holds this
// model (MachineSpecs.tsx:908-916) and deliberately does NOT clearSelectedModel
// on unmount — the catalog detail page owns that lifecycle, and clearing here
// caused a "Model not found" race. Both behaviours are kept.
//
// RENDERING IS ALSO ID-GUARDED, which v1 is not. Because the slot is shared, an
// earlier catalog visit can leave a DIFFERENT model in it, and v1 renders that
// model's spec sheet under this machine until the new fetch lands. The guard
// (`selectedModel.id === catalogModelId`) is the same one MachineProfileV2
// applies to `customerMachinery.machine` for exactly this reason.
//
// FIX 5 (specs half): `getCatalogModelDetail.rejected` sets ONLY
// `selectedModelLoading = false` (machineCatalogSlice.ts:136-138) — there is no
// error field to read, so a failed fetch renders a blank tab forever. The
// failure is tracked here, from the dispatch's own promise.
//
// M2.8: the RENDER half moved to SpecSheetView.tsx so the catalog detail page
// draws the same sheet from the same code (see that file's header for the §2.3
// "reuse MachineSpecs" deviation). Everything tab-specific stayed here — the
// de-duplicated fetch, the shared-slot id guard and the error state. The
// toolbar, description blocks, group dividers, section list and the
// metric/collapse state moved verbatim; the tab renders identically.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, V2_PHONE, useV2MediaQuery } from "../../useV2MediaQuery";
import { getCatalogModelDetail } from "../../../machineCatalogSlice";
import RetryNotice from "../../feedback/RetryNotice";
import { TabCard, TabEmpty } from "./tabAtoms";
import { SkeletonList } from "./tabSkeletons";
import { resolveSpecSheet } from "./specResolve";
import SpecSheetView, { specSheetHasContent } from "./SpecSheetView";

export interface SpecificationsTabV2Props {
  catalogModelId: string;
}

const SpecificationsTabV2: React.FC<SpecificationsTabV2Props> = ({ catalogModelId }) => {
  const dispatch = useAppDispatch();
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  // F-A: the module's ONE phone predicate, evaluated as the query the CSS
  // evaluates. Identical to `innerWidth < 768` at every integer width.
  const isPhone = useV2MediaQuery(V2_PHONE);
  // THIS TAB IS THE PROFILE HOST of the shared spec sheet, and it is the only
  // one: the two katalóg detail surfaces mount the same component. That is what
  // `profileHost` below tells the sheet, and it gates board 23's toolbar
  // reorder alone (review F7). `touch` is the separate, viewport-wide 44px law,
  // which the katalóg's mobile detail passes too — see both prop docblocks in
  // SpecSheetView. Both hooks are called unconditionally on their own lines.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

  const selectedModel = useAppSelector((s) => s.machineCatalog.selectedModel);
  const loading = useAppSelector((s) => s.machineCatalog.selectedModelLoading);

  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    dispatch(getCatalogModelDetail(catalogModelId))
      .unwrap()
      .catch(() => setFailed(true));
  }, [dispatch, catalogModelId]);

  useEffect(() => {
    if (selectedModel?.id === catalogModelId) {
      setFailed(false);
      return;
    }
    load();
    // `selectedModel?.id` is read, not depended on: re-running when the slot
    // changes is exactly the loop the de-dup guard exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogModelId, load]);

  const currentLang = getCurrentLanguage();
  const model = selectedModel?.id === catalogModelId ? selectedModel : undefined;

  const sheet = useMemo(
    () => resolveSpecSheet(model?.specSections, currentLang),
    [model, currentLang]
  );

  const hasContent = specSheetHasContent(sheet);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      {/* Remounted per model so the collapse/metric state inside the view
          resets on a machine switch — the "nothing survives a machine change"
          doctrine, previously the `setCollapsed(new Set())` in the effect
          above. */}
      <SpecSheetView
        key={catalogModelId}
        sheet={sheet}
        label={translate("myMachinery:v2.tabs.specs.intro")}
        phone={isPhone}
        touch={belowDesktop}
        profileHost
        notices={
          <React.Fragment>
            {failed && (
              <RetryNotice
                title={translate("myMachinery:v2.tabs.specs.loadFailed")}
                hint={translate("myMachinery:v2.tabs.loadFailedHint")}
                onRetry={load}
                busy={loading}
              />
            )}

            {loading && !model && (
              <TabCard>
                <SkeletonList rows={4} height={38} />
              </TabCard>
            )}

            {/* §3: a loaded model with nothing to show says so; a model that
                never arrived (and did not fail) renders nothing at all, as v1
                does. */}
            {!loading && !failed && model && !hasContent && (
              <TabEmpty title={translate("myMachinery:noSpecifications")} />
            )}
          </React.Fragment>
        }
      />
    </div>
  );
};

export default SpecificationsTabV2;
